// Tufts University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Tufts' Common Data Set 2025-2026 (CDS_2025-2026.pdf, corrected 9/8/2026
//    in section H only), linked from Institutional Research's Common Data
//    Set page: C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table, C8 test policy for Fall 2027, C21/C22 early plans, G1
//    tuition, fees and food and housing for 2026-2027. G5 is left blank.
//    Quirk: the C1 sex table sums to 33,395 applied, 3,612 admitted and
//    1,764 enrolled; the residency table's totals are 33,415, 3,613 and
//    1,765. The page uses the sex table (the C1 totals). The residency table
//    also has 8 applicants and 1 admit of unknown residency, not shown here.
//    C21's count block is still headed "For the Fall 2024 entering class"
//    (the form's own heading, also seen in other schools' 2025-2026 files);
//    the 2024-2025 CDS left those counts blank, so these (3,344 and 1,071)
//    are read as the 2025-2026 edition's Fall 2025 figures.
//  - admissions.tufts.edu: Applying to Tufts, Application Checklist and
//    Deadlines, Early Decision, SAT and ACT Tests, Applying to SMFA at
//    Tufts, School of Engineering, Application FAQ. The deadline pages give
//    the Fall 2027 dates (November 2, 2026 and January 4, 2027). Quirk: the
//    checklist page gives Early Decision II notification as "Early
//    February", the Early Decision page as "Mid-February"; the page uses the
//    checklist and says so.
//  - Tufts Student Financial Services, Undergraduate Cost of Attendance,
//    2026-27 (on campus, first-years): the same tuition, fees, housing and
//    food as the CDS, plus books and supplies and personal expenses.
//    Transportation is listed as "Varies", so it is not in the total.
//  - College Scorecard, school id 168148 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Tufts Common Data Set 2025-2026',
        url: 'https://provost.tufts.edu/institutionalresearch/common-data-set/',
        read: '2026-10-01',
    },
    applying: {
        label: 'Tufts Admissions, Applying to Tufts',
        url: 'https://admissions.tufts.edu/apply/applying-to-tufts/',
        read: '2026-10-01',
    },
    checklist: {
        label: 'Tufts Admissions, Application Checklist and Deadlines',
        url: 'https://admissions.tufts.edu/apply/applying-to-tufts/checklist-and-deadlines/',
        read: '2026-10-01',
    },
    ed: {
        label: 'Tufts Admissions, Early Decision',
        url: 'https://admissions.tufts.edu/apply/applying-to-tufts/early-decision/',
        read: '2026-10-01',
    },
    tests: {
        label: 'Tufts Admissions, SAT and ACT Tests',
        url: 'https://admissions.tufts.edu/apply/applying-to-tufts/sat-and-act-tests/',
        read: '2026-10-01',
    },
    smfa: {
        label: 'Tufts Admissions, Applying to SMFA at Tufts',
        url: 'https://admissions.tufts.edu/apply/applying-to-tufts/applying-to-smfa-at-tufts/',
        read: '2026-10-01',
    },
    engineering: {
        label: 'Tufts Admissions, School of Engineering',
        url: 'https://admissions.tufts.edu/apply/applying-to-tufts/school-of-engineering/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Tufts Student Financial Services, Undergraduate Cost of Attendance',
        url: 'https://students.tufts.edu/financial-services/undergrad-aid/award-letter/undergraduate-cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?168148-Tufts-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'tufts-university',
    kbId: 'tufts_university',
    name: 'Tufts University',
    shortName: 'Tufts',
    location: 'Medford, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Tufts brand color guidelines); decoration only.
    branding: {
        primary: '#002E6D',
        secondary: '#3D2E25',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/tufts_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 33395,
        admitted: 3612,
        enrolled: 1764,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4733, admitted: 659 },
            { group: 'Out-of-state', applied: 19414, admitted: 2422 },
            { group: 'International', applied: 9260, admitted: 531 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Tufts\' Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set reports Early Decision I and II together for fall 2025. Tufts has no early action plan.',
        rows: [
            { plan: 'Early Decision I and II', applied: 3344, admitted: 1071 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of Tufts\' schools or to the combined degree, and you are considered only for the one you choose:',
        rows: [
            { school: 'School of the Museum of Fine Arts (BFA, and the 5-year BFA plus BA or BS)', requirement: 'A studio art portfolio of 12 to 20 finished pieces from the past 2 years, uploaded through the Applicant Status Page. It is due November 4, 2026 for Early Decision I and January 6, 2027 for Early Decision II and Regular Decision.', source: 'smfa' },
            { school: 'School of Engineering', requirement: 'The same application as every first-year applicant. Tufts strongly encourages four years of math and science in high school, including physics and calculus where available.', source: 'engineering' },
        ],
        note: 'Students in any school may take classes across all three.',
        source: 'applying',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 2', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 4', decision: 'Early February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'By April 1', binding: false },
        ],
        note: 'The school report and recommendations are due November 20 for Early Decision I, January 11 for Early Decision II and January 17 for Regular Decision. Mid-year grades are due February 15. Tufts\' Early Decision page gives Early Decision II notification as mid-February.',
        source: 'checklist',
        binding: 'Early Decision is binding: if admitted, you have agreed to come to Tufts. You, a parent or guardian, and your school counselor sign the Early Decision Agreement. If you are denied, you cannot apply again that year; if you are deferred, you are read with Regular Decision, the commitment ends, and you hear by April 1.',
        bindingSource: 'ed',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Tufts is test-optional: you choose whether to send SAT or ACT scores, and Tufts will not assume your scores are low if you do not. Tufts encourages sending an SAT of 1300 or higher or an ACT of 28 or higher. Your choice is fixed once you submit. Tufts superscores both tests and lets you enter your own scores on the application.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 74862 },
            { item: 'Required fees', amount: 1696 },
            { item: 'Housing (on campus)', amount: 11220 },
            { item: 'Food (meal plan)', amount: 9374 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Personal expenses', amount: 1846 },
        ],
        total: 99998,
        note: 'For a first-year student living on campus. Tufts lists transportation as varying with how far you live from campus, so it is not in this total. Health insurance is extra unless you waive it.',
        source: 'costs',
        netPrice: {
            average: 38797,
            byIncome: [
                { income: '$0 to $30,000', amount: 4195 },
                { income: '$30,001 to $48,000', amount: 10240 },
                { income: '$48,001 to $75,000', amount: 16897 },
                { income: '$75,001 to $110,000', amount: 29497 },
                { income: 'Over $110,000', amount: 57862 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Tufts\' aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
